import { Crown, Users, Building2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const AgencyTab = ({
  isAgencyMode, setIsAgencyMode,
  agencyClients, setAgencyClients,
  setSelectedClient,
  credits, setCredits, AGENCY_COST
}) => (
  <div className="space-y-6">
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-600">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Agency Mode</h2>
            <p className="text-gray-500 text-sm">Manage multiple clients, team members, and white-label exports</p>
          </div>
        </div>
        <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-medium">{AGENCY_COST} credits/mo</span>
      </div>

      {!isAgencyMode ? (
        <div className="text-center py-12">
          <Crown className="h-16 w-16 mx-auto mb-4 text-yellow-500" />
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Upgrade to Agency Mode</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Manage multiple clients, add team members, white-label exports, and priority generation queue.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
            {[
              { Icon: Users, label: "Multi-Client", desc: "Manage 10+ clients" },
              { Icon: Building2, label: "White Label", desc: "Remove Veyronix branding" },
              { Icon: Zap, label: "Priority Queue", desc: "2x faster generations" },
            ].map(({ Icon, label, desc }) => (
              <div key={label} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                <div className="font-medium">{label}</div>
                <div className="text-xs text-gray-500">{desc}</div>
              </div>
            ))}
          </div>
          <Button 
            onClick={() => {
              if (credits >= AGENCY_COST) {
                setIsAgencyMode(true);
                setCredits(c => c - AGENCY_COST);
                toast.success("Agency Mode activated!");
              } else {
                toast.error(`Insufficient credits. Need ${AGENCY_COST}`);
              }
            }}
            className="bg-gradient-to-r from-yellow-500 to-amber-600 px-8 py-6 text-lg"
          >
            <Crown className="h-5 w-5 mr-2" />
            Activate Agency Mode ({AGENCY_COST} credits/month)
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="p-4 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-lg border border-yellow-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6 text-yellow-600" />
              <div>
                <div className="font-bold text-gray-800 dark:text-white">Agency Mode Active</div>
                <div className="text-sm text-gray-600">All premium features unlocked</div>
              </div>
            </div>
            <span className="px-3 py-1 bg-yellow-500 text-white rounded-full text-sm font-medium">PRO</span>
          </div>

          <div>
            <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Your Clients
            </h3>
            {agencyClients.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-500 mb-3">No clients added yet</p>
                <Button size="sm" variant="outline" onClick={() => {
                  setAgencyClients([...agencyClients, { id: Date.now(), name: "New Client", email: "client@example.com" }]);
                }}>
                  <Users className="h-3 w-3 mr-1" />
                  Add First Client
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {agencyClients.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-800 dark:text-white">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.email}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => {
                      setSelectedClient(client);
                      toast.success(`Switched to ${client.name}`);
                    }}>
                      Select
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => {
                  setAgencyClients([...agencyClients, { id: Date.now(), name: `Client ${agencyClients.length + 1}`, email: `client${agencyClients.length + 1}@example.com` }]);
                }}>
                  + Add Client
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);
